import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface NotificationPreferences {
  enabled: boolean;
  sound: boolean;
  desktop: boolean;
  offerCreated: boolean;
  offerAccepted: boolean;
  offerCancelled: boolean;
  offerReceived: boolean;
  transactionSuccess: boolean;
  transactionFailed: boolean;
  priceAlerts: boolean;
}

interface PreferencesState {
  // User Settings
  displayName: string;
  preferredCurrency: 'XRP' | 'USD';

  // Notification Settings
  notificationPreferences: NotificationPreferences;

  // Actions
  setDisplayName: (name: string) => void;
  setPreferredCurrency: (currency: 'XRP' | 'USD') => void;
  setNotificationPreference: (
    key: keyof NotificationPreferences,
    enabled: boolean
  ) => void;
  resetPreferences: () => void;
}

const initialState = {
  displayName: '',
  preferredCurrency: 'XRP' as const,
  notificationPreferences: {
    enabled: true,
    sound: true,
    desktop: false,
    offerCreated: true,
    offerAccepted: true,
    offerCancelled: true,
    offerReceived: true,
    transactionSuccess: true,
    transactionFailed: true,
    priceAlerts: false,
  },
};

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      ...initialState,

      setDisplayName: (displayName) => {
        if (displayName.length > 50) {
          console.warn('Display name exceeds 50 characters');
          return;
        }
        set({ displayName });
      },

      setPreferredCurrency: (preferredCurrency) => {
        if (preferredCurrency !== 'XRP' && preferredCurrency !== 'USD') {
          console.error('Invalid currency:', preferredCurrency);
          return;
        }
        set({ preferredCurrency });
      },

      setNotificationPreference: (key, enabled) => {
        set((state) => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            [key]: enabled,
          },
        }));
      },

      resetPreferences: () => set(initialState),
    }),
    {
      name: 'xrpl_marketplace_preferences',
      version: 1,
      partialize: (state) => ({
        displayName: state.displayName,
        preferredCurrency: state.preferredCurrency,
        notificationPreferences: state.notificationPreferences,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migration from version 0 to 1: add notification preferences
          return {
            ...persistedState,
            notificationPreferences: initialState.notificationPreferences,
          };
        }
        return persistedState as PreferencesState;
      },
    }
  )
);
